import { createSlice, PayloadAction } from '@reduxjs/toolkit'

export interface ErrorBanner {
    id: string
    message: string
    type: 'error' | 'warning' | 'info'
    dismissible: boolean
}

export interface RetryModalState {
    isOpen: boolean
    title: string
    message: string
    onRetry: (() => void) | null
    onCancel: (() => void) | null
}

export interface ErrorSliceState {
    banners: ErrorBanner[]
    retryModal: RetryModalState
    networkStatus: 'online' | 'offline'
}

const initialRetryModalState: RetryModalState = {
    isOpen: false,
    title: '',
    message: '',
    onRetry: null,
    onCancel: null,
}

const initialState: ErrorSliceState = {
    banners: [],
    retryModal: initialRetryModalState,
    networkStatus: 'online',
}

export const errorSlice = createSlice({
    name: 'error',
    initialState,
    reducers: {
        addBanner: (state, action: PayloadAction<ErrorBanner>) => {
            const exists = state.banners.some((b) => b.id === action.payload.id)
            if (!exists) {
                state.banners.push(action.payload)
            }
        },
        removeBanner: (state, action: PayloadAction<string>) => {
            state.banners = state.banners.filter((b) => b.id !== action.payload)
        },
        clearBanners: (state) => {
            state.banners = []
        },
        setRetryModal: (
            state,
            action: PayloadAction<Omit<RetryModalState, 'isOpen'>>
        ) => {
            state.retryModal = {
                ...action.payload,
                isOpen: true,
            }
        },
        clearRetryModal: (state) => {
            state.retryModal = initialRetryModalState
        },
        setNetworkStatus: (
            state,
            action: PayloadAction<'online' | 'offline'>
        ) => {
            state.networkStatus = action.payload
        },
    },
})

export const {
    addBanner,
    removeBanner,
    clearBanners,
    setRetryModal,
    clearRetryModal,
    setNetworkStatus,
} = errorSlice.actions

export const errorReducer = errorSlice.reducer
